import { z } from 'zod';
import { FileSummarySchema, PersonRefSchema, SiteRefSchema } from './property';

/**
 * Fleet M2 (docs/specs/fleet.md §8, D63–D65): repairs, fines and incidents as Aroo cases on one
 * case/timeline/document pattern; grey-fleet declarations; encrypted secrets with audited
 * reveal; vehicle assignment and off-road from Aroo; licence-check consent captured in Aroo.
 * Costs (repair parts/labour, fine amounts) are omitted server-side for viewers without
 * fleet.costs.view at the case's branch (D65).
 */

const IsoDate = z.iso.date();
const Instant = z.iso.datetime();
const Wall = z.string().regex(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/);
const Money = z.string().regex(/^-?\d+(\.\d{1,2})?$/, 'Use pounds and pence, e.g. 35.00');

export const FLEET_M2_PERMISSIONS = {
  report: 'fleet.cases.report',
  approveRepairs: 'fleet.repairs.approve',
  costs: 'fleet.costs.view',
} as const;

// Vocabularies --------------------------------------------------------------------------

export const FleetCaseKindSchema = z.enum(['repair', 'fine', 'incident']);
export type FleetCaseKind = z.infer<typeof FleetCaseKindSchema>;

export const FLEET_CASE_KIND_LABELS: Record<FleetCaseKind, string> = {
  repair: 'Repair',
  fine: 'Fine',
  incident: 'Incident',
};

/** The old fines workflow (D63): create → confirm driver → accept/dispute → investigation → paid → complete. */
export const FineStatusSchema = z.enum([
  'pending',
  'driver_disputed',
  'confirmed',
  'fine_disputed',
  'awaiting_payment',
  'paid',
  'complete',
  'cancelled',
]);
export type FineStatus = z.infer<typeof FineStatusSchema>;

export const RepairStatusSchema = z.enum([
  'open',
  'approved',
  'rejected',
  'in_progress',
  'invoice_uploaded',
  'complete',
  'maintenance_rejected',
  'maintenance_closed',
]);
export type RepairStatus = z.infer<typeof RepairStatusSchema>;

export const IncidentStatusSchema = z.enum([
  'pending',
  'insurer_notified',
  'approved_repair',
  'write_off',
  'closed',
]);
export type IncidentStatus = z.infer<typeof IncidentStatusSchema>;

export const FLEET_CASE_STATUS_LABELS: Record<string, string> = {
  // fines
  pending: 'Awaiting driver confirmation',
  driver_disputed: 'Driver disputed: confirm the driver',
  confirmed: "Awaiting the driver's response",
  fine_disputed: 'Disputed: under investigation',
  awaiting_payment: 'Awaiting payment',
  paid: 'Paid',
  complete: 'Complete',
  cancelled: 'Cancelled',
  // repairs
  open: 'Reported, awaiting approval',
  approved: 'Approved',
  rejected: 'Rejected',
  in_progress: 'In progress',
  invoice_uploaded: 'Invoice received',
  maintenance_rejected: 'Declined under maintenance',
  maintenance_closed: 'Closed under maintenance',
  // incidents
  insurer_notified: 'Insurer notified',
  approved_repair: 'Repair approved',
  write_off: 'Write-off',
  closed: 'Closed',
};

/** Incident pending shares the fines' "pending" key; the screens use this for incidents. */
export const INCIDENT_PENDING_LABEL = 'Pending';

export const IncidentTypeSchema = z.enum([
  'Breakdown',
  'Collision',
  'Damage',
  'Theft/Attempted Theft',
  'Fire',
  'Vandalism',
  'Other',
]);
export type IncidentType = z.infer<typeof IncidentTypeSchema>;

export const SeveritySchema = z.enum(['minor', 'moderate', 'severe']);
export const FaultSchema = z.enum(['At Fault', 'Non Fault', 'Unknown', 'N/A']);
export const PrioritySchema = z.enum(['urgent', 'normal', 'low']);

export const FleetCaseActionSchema = z.enum([
  // every kind
  'note',
  // fines
  'confirm_driver',
  'dispute_driver',
  'accept',
  'dispute_fine',
  'complete_investigation',
  'mark_paid',
  'complete',
  'cancel',
  // repairs
  'approve',
  'reject',
  'start',
  'invoice_received',
  'hold',
  'release',
  'maintenance_reject',
  'maintenance_close',
  // incidents
  'set_status',
  'close',
  'reopen',
]);
export type FleetCaseAction = z.infer<typeof FleetCaseActionSchema>;

export const FLEET_CASE_ACTION_LABELS: Record<FleetCaseAction, string> = {
  note: 'Add a note',
  confirm_driver: 'Confirm the driver',
  dispute_driver: 'Not this driver',
  accept: 'Accept the fine',
  dispute_fine: 'Dispute the fine',
  complete_investigation: 'Complete the investigation',
  mark_paid: 'Mark paid',
  complete: 'Complete',
  cancel: 'Cancel the fine',
  approve: 'Approve',
  reject: 'Reject',
  start: 'Start the work',
  invoice_received: 'Invoice received',
  hold: 'Put on hold',
  release: 'Take off hold',
  maintenance_reject: 'Declined under maintenance',
  maintenance_close: 'Close under maintenance',
  set_status: 'Update status',
  close: 'Close',
  reopen: 'Reopen',
};

// Cases ------------------------------------------------------------------------------------

export const FleetCaseSummarySchema = z.object({
  id: z.uuid(),
  kind: FleetCaseKindSchema,
  ref: z.string(),
  /** The old system's number (FINE-0089, POV00305, a case number) once it has one. */
  legacyRef: z.string().nullable(),
  status: z.string(),
  open: z.boolean(),
  title: z.string(),
  reg: z.string().nullable(),
  vehicle: z.object({ id: z.uuid(), reg: z.string() }).nullable(),
  site: SiteRefSchema.nullable(),
  driver: PersonRefSchema.nullable(),
  isPool: z.boolean(),
  /** Issue date (fine), occurred (incident) or raised (repair). */
  date: IsoDate.nullable(),
  points: z.number().int().nullable(),
  priority: PrioritySchema.nullable(),
  onHold: z.boolean(),
  severity: SeveritySchema.nullable(),
  /** Fine amount / repair total; null unless the viewer may see costs here (D65). */
  cost: Money.nullable(),
  reportedBy: PersonRefSchema.nullable(),
  reportedAt: Instant,
});
export type FleetCaseSummary = z.infer<typeof FleetCaseSummarySchema>;

export const FleetCasesResponseSchema = z.object({
  cases: z.array(FleetCaseSummarySchema),
  can: z.object({
    manage: z.boolean(),
    report: z.boolean(),
    costs: z.boolean(),
  }),
});
export type FleetCasesResponse = z.infer<typeof FleetCasesResponseSchema>;

export const FleetCaseEventSchema = z.object({
  id: z.uuid(),
  at: Instant,
  actor: PersonRefSchema.nullable(),
  /** "Old system" for timeline rows that came from dt_spar. */
  actorLabel: z.string().nullable(),
  kind: z.string(),
  fromStatus: z.string().nullable(),
  toStatus: z.string().nullable(),
  note: z.string().nullable(),
});
export type FleetCaseEvent = z.infer<typeof FleetCaseEventSchema>;

export const FleetCaseDetailSchema = z.object({
  case: FleetCaseSummarySchema.extend({
    description: z.string().nullable(),
    location: z.string().nullable(),
    onHoldNote: z.string().nullable(),
    isMaintained: z.boolean(),
    repairer: z.string().nullable(),
    estimatedCompletion: IsoDate.nullable(),
    completedOn: IsoDate.nullable(),
    fineType: z.object({ id: z.number().int(), name: z.string() }).nullable(),
    authorityRef: z.string().nullable(),
    issueDate: IsoDate.nullable(),
    dueDate: IsoDate.nullable(),
    paidOn: IsoDate.nullable(),
    disputeCount: z.number().int(),
    investigationDone: z.boolean(),
    occurredAt: Wall.nullable(),
    incidentType: z.string().nullable(),
    fault: z.string().nullable(),
    legacyNotes: z.string().nullable(),
    closedAt: Instant.nullable(),
    /** Cost fields: null unless `can.costs`. */
    costs: z
      .object({
        partsCost: Money.nullable(),
        labourCost: Money.nullable(),
        amount: Money.nullable(),
        earlyAmount: Money.nullable(),
        earlyDue: IsoDate.nullable(),
        repairCost: Money.nullable(),
        writeOffCost: Money.nullable(),
      })
      .nullable(),
  }),
  events: z.array(FleetCaseEventSchema),
  /** Photos, PCN letters, evidence: anyone who can see the case. */
  files: z.array(FileSummarySchema),
  /** Quotes and invoices (they show the cost): cost viewers only. */
  costFiles: z.array(FileSummarySchema),
  /** Documents still on the old system's disk (not copied into Aroo yet). */
  legacyDocuments: z.number().int(),
  can: z.object({
    actions: z.array(FleetCaseActionSchema),
    addFiles: z.boolean(),
    addCostFiles: z.boolean(),
    edit: z.boolean(),
    costs: z.boolean(),
  }),
});
export type FleetCaseDetail = z.infer<typeof FleetCaseDetailSchema>;

/** POST /fleet/cases (multipart): a BM/RM reports a defect or an incident, with photos. */
export const FleetReportFieldsSchema = z.discriminatedUnion('kind', [
  z.object({
    kind: z.literal('repair'),
    vehicleId: z.uuid(),
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().min(3).max(4000),
    priority: PrioritySchema.default('normal'),
  }),
  z.object({
    kind: z.literal('incident'),
    vehicleId: z.uuid(),
    occurredAt: Wall,
    incidentType: IncidentTypeSchema,
    severity: SeveritySchema,
    location: z.string().trim().max(255).optional(),
    description: z.string().trim().min(3).max(4000),
    personId: z.uuid().optional(),
  }),
]);
export type FleetReportFields = z.infer<typeof FleetReportFieldsSchema>;

/** POST /fleet/fines (fleet admin). The driver defaults to whoever had the vehicle that day. */
export const FineCreateSchema = z.object({
  vehicleId: z.uuid(),
  fineTypeId: z.number().int().positive(),
  authorityRef: z.string().trim().max(50).nullable().optional(),
  issueDate: IsoDate,
  dueDate: IsoDate,
  amount: Money,
  earlyAmount: Money.nullable().optional(),
  earlyDue: IsoDate.nullable().optional(),
  points: z.number().int().min(0).max(12).nullable().optional(),
  location: z.string().trim().min(2).max(255),
  description: z.string().trim().max(4000).nullable().optional(),
  /** Leave out to use the vehicle's driver on the issue date. */
  personId: z.uuid().nullable().optional(),
});
export type FineCreate = z.infer<typeof FineCreateSchema>;

export const FleetCaseActionWriteSchema = z.object({
  action: FleetCaseActionSchema,
  note: z.string().trim().max(4000).nullable().optional(),
  /** confirm_driver: the driver (leave out to confirm the one already named). */
  personId: z.uuid().nullable().optional(),
  /** mark_paid. */
  paidOn: IsoDate.nullable().optional(),
  /** complete_investigation: uphold → awaiting payment; void → cancelled. */
  outcome: z.enum(['uphold', 'void']).optional(),
  /** set_status (incidents). */
  status: IncidentStatusSchema.optional(),
});
export type FleetCaseActionWrite = z.infer<typeof FleetCaseActionWriteSchema>;

export const FleetCasePatchSchema = z
  .object({
    title: z.string().trim().min(3).max(200),
    description: z.string().trim().max(4000).nullable(),
    location: z.string().trim().max(255).nullable(),
    priority: PrioritySchema,
    repairer: z.string().trim().max(200).nullable(),
    estimatedCompletion: IsoDate.nullable(),
    partsCost: Money.nullable(),
    labourCost: Money.nullable(),
    fineTypeId: z.number().int().positive(),
    authorityRef: z.string().trim().max(50).nullable(),
    amount: Money,
    earlyAmount: Money.nullable(),
    earlyDue: IsoDate.nullable(),
    points: z.number().int().min(0).max(12).nullable(),
    issueDate: IsoDate,
    dueDate: IsoDate,
    severity: SeveritySchema,
    fault: FaultSchema.nullable(),
    repairCost: Money.nullable(),
    writeOffCost: Money.nullable(),
  })
  .partial();
export type FleetCasePatch = z.infer<typeof FleetCasePatchSchema>;

export const FineTypeSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  defaultPoints: z.number().int(),
});
export type FineType = z.infer<typeof FineTypeSchema>;
export const FineTypesResponseSchema = z.object({ types: z.array(FineTypeSchema) });

// Assignment and off-road --------------------------------------------------------------------

export const VehicleAssignWriteSchema = z.object({
  /** null with pool=false unassigns. */
  personId: z.uuid().nullable(),
  pool: z.boolean().default(false),
  /** Fleet admin only: assign a driver whose licence says "not authorised" (D65), audited. */
  overrideReason: z.string().trim().min(5).max(500).nullable().optional(),
});
export type VehicleAssignWrite = z.infer<typeof VehicleAssignWriteSchema>;

export const VehicleAssignResponseSchema = z.object({
  ok: z.literal(true),
  /** Another vehicle the driver held, released by this assignment. */
  released: z.object({ id: z.uuid(), reg: z.string() }).nullable(),
});
export type VehicleAssignResponse = z.infer<typeof VehicleAssignResponseSchema>;

export const VehicleVorWriteSchema = z.object({
  offRoad: z.boolean(),
  reason: z.string().trim().min(2).max(100).nullable().optional(),
  notes: z.string().trim().max(1000).nullable().optional(),
});
export type VehicleVorWrite = z.infer<typeof VehicleVorWriteSchema>;

// Secrets (D63) -------------------------------------------------------------------------------

export const FleetSecretKindSchema = z.enum(['fuel_pin', 'epod_password']);
export type FleetSecretKind = z.infer<typeof FleetSecretKindSchema>;

export const FLEET_SECRET_LABELS: Record<FleetSecretKind, string> = {
  fuel_pin: 'Fuel-card PIN',
  epod_password: 'ePOD password',
};

export const FleetSecretSetSchema = z.object({ value: z.string().min(1).max(64) });
export type FleetSecretSet = z.infer<typeof FleetSecretSetSchema>;

export const FleetSecretRevealResponseSchema = z.object({ value: z.string() });

export const FleetSecretSetResponseSchema = z.object({
  ok: z.literal(true),
  replaced: z.boolean(),
});

// Grey fleet declarations (D65) ---------------------------------------------------------------

export const GreyDeclarationStatusSchema = z.enum(['pending', 'approved', 'rejected', 'withdrawn']);
export type GreyDeclarationStatus = z.infer<typeof GreyDeclarationStatusSchema>;

export const GreyDeclarationSchema = z.object({
  id: z.uuid(),
  person: PersonRefSchema,
  site: SiteRefSchema.nullable(),
  reg: z.string(),
  make: z.string().nullable(),
  model: z.string().nullable(),
  businessCover: z.boolean(),
  insuranceExpiry: IsoDate,
  motDue: IsoDate.nullable(),
  status: GreyDeclarationStatusSchema,
  vehicleId: z.uuid().nullable(),
  createdAt: Instant,
  decidedAt: Instant.nullable(),
  decidedBy: PersonRefSchema.nullable(),
  decisionNote: z.string().nullable(),
  files: z.array(FileSummarySchema),
});
export type GreyDeclaration = z.infer<typeof GreyDeclarationSchema>;

export const GreyDeclarationsResponseSchema = z.object({
  declarations: z.array(GreyDeclarationSchema),
  canDecide: z.boolean(),
});
export type GreyDeclarationsResponse = z.infer<typeof GreyDeclarationsResponseSchema>;

/** POST /fleet/grey/declarations (multipart): the fields; the files are `insurance` and `mot`. */
export const GreyDeclarationFieldsSchema = z.object({
  reg: z.string().trim().min(2).max(16),
  make: z.string().trim().max(50).optional(),
  model: z.string().trim().max(50).optional(),
  businessCover: z.enum(['true', 'false']).transform((v) => v === 'true'),
  insuranceExpiry: IsoDate,
  motDue: IsoDate.optional(),
});
export type GreyDeclarationFields = z.infer<typeof GreyDeclarationFieldsSchema>;

export const GreyDecisionSchema = z.object({
  decision: z.enum(['approve', 'reject']),
  note: z.string().trim().max(500).nullable().optional(),
});
export type GreyDecision = z.infer<typeof GreyDecisionSchema>;

// Consent (D63) ---------------------------------------------------------------------------------

export const ConsentTextSchema = z.object({ version: z.string(), text: z.string() });
export type ConsentText = z.infer<typeof ConsentTextSchema>;

export const ConsentWriteSchema = z.object({
  /** The 16-character DVLA driving licence number (stored encrypted). */
  licenceNumber: z
    .string()
    .trim()
    .transform((v) => v.toUpperCase().replace(/\s+/g, ''))
    .pipe(z.string().regex(/^[A-Z9]{5}\d{6}[A-Z9]{2}\d[A-Z0-9]{2}$/, 'Check the licence number')),
  /** The signature drawn on screen, a PNG data URL. */
  signature: z
    .string()
    .regex(/^data:image\/png;base64,[A-Za-z0-9+/]+=*$/)
    .max(400_000),
  agree: z.literal(true),
  textVersion: z.string().max(20),
});
export type ConsentWrite = z.infer<typeof ConsentWriteSchema>;
